import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly, parseDateOnly } from '@/utils/DateOnlyHelpers'
import {
  ConflictInfo,
  CreateTaxAssumption,
  TaxAssumptionDetail,
  UpdateTaxAssumption,
  UpdateTaxAssumptionDetail,
} from '@repo/backend/crt'
import { parseTaxAssumptionType, TaxAssumptionType } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const taxAssumptionsSearchSchema = z.object({
  keyword: z.string().optional(),
  effectiveDateFrom: z.coerce.date().optional(),
  effectiveDateTo: z.coerce.date().optional(),
  type: z.array(z.custom<TaxAssumptionType>()).optional(),
  ...datatableSearchSchema.shape,
})

export type TaxAssumptionsSearch = z.infer<typeof taxAssumptionsSearchSchema>

export type TaxAssumptions = Awaited<ReturnType<typeof getTaxAssumptions>>
export type TaxAssumption = Awaited<ReturnType<typeof getTaxAssumptionById>>
export type TaxAssumptionDetailFromApi = TaxAssumption['details'][number]

type UseCreateTaxAssumptionArgs = {
  taxAssumptionDetails: CreateTaxAssumption
  queries: TaxAssumptionsSearch
  handleOnSuccess: (taxAssumptionId: number) => void
}

type UseUpdateTaxAssumptionArgs = QueryHandlers & {
  taxAssumptionId: number
}

type UseDeleteTaxAssumptionsArgs = QueryHandlers & {
  taxAssumptionIds: number[]
  queries: TaxAssumptionsSearch
}

type TaxAssumptionDetailsQueries = {
  materialGroupCode?: string[]
  brand?: string[]
  province?: string[]
  unmarkedProvince?: string[]
  channel?: string[]
  sort?: {
    id: string
    desc: boolean
  }
}

async function getTaxAssumptions(search: TaxAssumptionsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['pricing-excise']['tax-assumptions'].$get(
    {
      query: stringifyValues({
        ...search,
        effectiveDateFrom: search.effectiveDateFrom
          ? formatDateOnly(search.effectiveDateFrom)
          : undefined,
        effectiveDateTo: search.effectiveDateTo
          ? formatDateOnly(search.effectiveDateTo)
          : undefined,
      }),
    },
    { init: { signal } },
  )

  const taxAssumptions = await res.json()
  return {
    ...taxAssumptions,
    results: taxAssumptions.results.map((assumption) => ({
      ...assumption,
      effectiveDate: parseDateOnly(assumption.effectiveDate),
      type: parseTaxAssumptionType(assumption.type),
    })),
  }
}

async function getTaxAssumptionById(
  id: string,
  queries?: TaxAssumptionDetailsQueries,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['pricing-excise']['tax-assumptions'][':id'].$get(
    {
      param: { id },
      query: queries ? stringifyValues(queries) : {},
    },
    { init: { signal } },
  )

  const taxAssumption = await res.json()
  return {
    ...taxAssumption,
    effectiveDate: parseDateOnly(taxAssumption.effectiveDate),
    type: parseTaxAssumptionType(taxAssumption.type),
    details: taxAssumption.details.map((detail) => ({
      ...detail,
      // Convert from decimal (0.02) to percentage (2%)
      priceAdjustment: taxAssumption.isPercentage
        ? Number(detail.priceAdjustment) * 100
        : Number(detail.priceAdjustment),
    })),
  }
}

async function createTaxAssumption(taxAssumptionDetails: CreateTaxAssumption) {
  const api = getApi()

  const res = await api.crt['pricing-excise']['tax-assumptions'].$post({
    json: taxAssumptionDetails,
  })

  const taxAssumption = await res.json()
  return taxAssumption
}

async function updateTaxAssumption(taxAssumptionId: number, details: UpdateTaxAssumption) {
  const api = getApi()

  await api.crt['pricing-excise']['tax-assumptions'][':id'].$patch({
    param: { id: String(taxAssumptionId) },
    json: details,
  })
}

async function deleteTaxAssumptions(taxAssumptionIds: number[]) {
  const api = getApi()

  await api.crt['pricing-excise']['tax-assumptions'].bulk.$delete({
    json: { ids: taxAssumptionIds },
  })
}

async function createTaxAssumptionDetails(taxAssumptionId: string, details: TaxAssumptionDetail[]) {
  const api = getApi()

  const res = await api.crt['pricing-excise']['tax-assumptions'][':id'].details.$post({
    param: { id: taxAssumptionId },
    json: { details },
  })

  const result = await res.json()
  return {
    createdAssumptions: result.createdAssumptions.map(
      (detail: (typeof result.createdAssumptions)[number]) => ({
        ...detail,
        priceAdjustment: Number(detail.priceAdjustment),
      }),
    ),
    failedCreations: result.failedCreations,
  }
}

async function deleteTaxAssumptionDetail(taxAssumptionId: string, detailId: number) {
  const api = getApi()

  await api.crt['pricing-excise']['tax-assumptions'][':id'].details[':detailId'].$delete({
    param: { id: taxAssumptionId, detailId: String(detailId) },
  })
}

async function deleteTaxAssumptionDetails(taxAssumptionId: string, detailIds: number[]) {
  const api = getApi()

  await api.crt['pricing-excise']['tax-assumptions'][':id'].details.bulk.$delete({
    param: { id: taxAssumptionId },
    json: { ids: detailIds },
  })
}

async function updateTaxAssumptionDetails(
  taxAssumptionId: string,
  updates: Array<{ id: number; data: { priceAdjustment: number } }>,
) {
  const api = getApi()

  const res = await api.crt['pricing-excise']['tax-assumptions'][':id'].details.bulk.$patch({
    param: { id: taxAssumptionId },
    json: { updates },
  })

  const updatedDetails = await res.json()
  return updatedDetails.map((detail: (typeof updatedDetails)[number]) => ({
    ...detail,
    priceAdjustment: Number(detail.priceAdjustment),
  }))
}

async function updateTaxAssumptionDetail(
  taxAssumptionId: string,
  detailId: number,
  data: UpdateTaxAssumptionDetail,
): Promise<
  | { success: true; detail: TaxAssumptionDetailFromApi }
  | { success: false; conflicts: ConflictInfo[] }
> {
  const api = getApi()

  try {
    const res = await api.crt['pricing-excise']['tax-assumptions'][':id'].details[':detailId'].$put(
      {
        param: { id: taxAssumptionId, detailId: String(detailId) },
        json: data,
      },
    )

    const detail = await res.json()
    return {
      success: true,
      detail: {
        ...detail,
        priceAdjustment: Number((detail as { priceAdjustment: string }).priceAdjustment),
      } as TaxAssumptionDetailFromApi,
    }
  } catch (error) {
    // Hono RPC client throws the parsed body directly for 409 responses
    if (error && typeof error === 'object' && 'conflicts' in error) {
      return { success: false, conflicts: (error as { conflicts: ConflictInfo[] }).conflicts }
    }
    throw error
  }
}

export function useTaxAssumptions(queries: TaxAssumptionsSearch, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['taxAssumptions', queries],
    queryFn: ({ signal }) => getTaxAssumptions(queries, signal),
    enabled: options?.enabled ?? true,
  })
}

export const taxAssumptionByIdQueryOptions = (
  id: string,
  queries?: TaxAssumptionDetailsQueries,
) => ({
  queryKey: ['taxAssumptionById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getTaxAssumptionById(id, queries, signal),
})

export function useTaxAssumptionById(id: string, queries?: TaxAssumptionDetailsQueries) {
  return useQuery({
    ...taxAssumptionByIdQueryOptions(id, queries),
    refetchOnWindowFocus: false,
  })
}

export function useCreateTaxAssumption({
  taxAssumptionDetails,
  queries,
  handleOnSuccess,
}: UseCreateTaxAssumptionArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['taxAssumptions', queries],
    mutationFn: () => createTaxAssumption(taxAssumptionDetails),
    onSuccess: (taxAssumption) => {
      handleOnSuccess(taxAssumption.id)

      queryClient.invalidateQueries({
        queryKey: ['taxAssumptions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptions.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptions.createFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateTaxAssumption({
  taxAssumptionId,
  handleOnSuccess,
}: UseUpdateTaxAssumptionArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateTaxAssumption', taxAssumptionId],
    mutationFn: (details: UpdateTaxAssumption) => updateTaxAssumption(taxAssumptionId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['taxAssumptionById', String(taxAssumptionId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptions.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptions.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteTaxAssumptions({
  taxAssumptionIds,
  queries,
  handleOnSuccess,
}: UseDeleteTaxAssumptionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteTaxAssumptions'],
    mutationFn: () => deleteTaxAssumptions(taxAssumptionIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['taxAssumptions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptions.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptions.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useCreateTaxAssumptionDetails(taxAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createTaxAssumptionDetails', taxAssumptionId],
    mutationFn: (details: TaxAssumptionDetail[]) =>
      createTaxAssumptionDetails(taxAssumptionId, details),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ['taxAssumptionById', taxAssumptionId],
      })

      if (result.failedCreations.length > 0) {
        toast.warning(t('toast.warning', { ns: 'common' }), {
          description: t('taxAssumptionDetails.partialSuccess', {
            ns: 'crt',
            created: result.createdAssumptions.length,
            failed: result.failedCreations.length,
          }),
          className: toastClassName,
        })
        return
      }

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptionDetails.createSuccess', {
          ns: 'crt',
          count: result.createdAssumptions.length,
        }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptionDetails.createFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteTaxAssumptionDetail(taxAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteTaxAssumptionDetail', taxAssumptionId],
    mutationFn: (detailId: number) => deleteTaxAssumptionDetail(taxAssumptionId, detailId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['taxAssumptionById', taxAssumptionId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptionDetails.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptionDetails.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteTaxAssumptionDetails(taxAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteTaxAssumptionDetails', taxAssumptionId],
    mutationFn: (detailIds: number[]) => deleteTaxAssumptionDetails(taxAssumptionId, detailIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['taxAssumptionById', taxAssumptionId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptionDetails.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptionDetails.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateTaxAssumptionDetails(taxAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateTaxAssumptionDetails', taxAssumptionId],
    mutationFn: (updates: Array<{ id: number; data: { priceAdjustment: number } }>) =>
      updateTaxAssumptionDetails(taxAssumptionId, updates),
    onSuccess: (updatedDetails) => {
      queryClient.invalidateQueries({
        queryKey: ['taxAssumptionById', taxAssumptionId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('taxAssumptionDetails.updateSuccess', {
          ns: 'crt',
          count: updatedDetails.length,
        }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptionDetails.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateTaxAssumptionDetail(taxAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateTaxAssumptionDetail', taxAssumptionId],
    mutationFn: ({ detailId, data }: { detailId: number; data: UpdateTaxAssumptionDetail }) =>
      updateTaxAssumptionDetail(taxAssumptionId, detailId, data),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({
          queryKey: ['taxAssumptionById', taxAssumptionId],
        })

        toast.success(t('toast.success', { ns: 'common' }), {
          description: t('taxAssumptionDetails.updateSuccess', {
            ns: 'crt',
            count: 1,
          }),
          className: toastClassName,
        })
      }
    },
    onError: (error: unknown) => {
      // This should only be called for actual errors, not conflicts
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.taxAssumptionDetails.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
